import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import cardApi from './upgradeCard.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchUpgradeCardApi = createAsyncThunk(
    'cardSaga/upgradeCard/fetchUpgradeCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await cardApi.getAll();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleUpgradeCardPublishedStateApi = createAsyncThunk(
    'cardSaga/upgradeCard/toggleUpgradeCardPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);

            throw rejectWithValue([]); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const createUpgradeCardApi = createAsyncThunk(
    'cardSaga/upgradeCard/createUpgradeCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await cardApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeUpgradeCardApi = createAsyncThunk(
    'cardSaga/upgradeCard/storeUpgradeCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.store(values);

            alertSwal('success', 'Card generated successfully!');
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValuePower`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-powers
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const showUpgradeCardApi = createAsyncThunk(
    'cardSaga/upgradeCard/showUpgradeCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await cardApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateUpgradeCardApi = createAsyncThunk(
    'cardSaga/upgradeCard/updateUpgradeCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.update(id, values);
            alertSwal('success', 'Card updated successfully!');

            return {
                id: values.id,
                name: values.name,
            };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const deleteUpgradeCardItemApi = createAsyncThunk(
    'cardSaga/upgradeCard/deleteUpgradeCardItemApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await cardApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const upgradeCardSlice = createSlice({
    name: 'cardSaga/upgradeCard',
    initialState: {
        rows: [],
        emptyModel: {},
        editedModel: {},
        frontSides: [],
        backSides: [],
        allPictures: [],
        templates: [],
    },
    reducers: {
        clearEditedUpgradeCard(state) {
            state.editedModel = {};
        },
        updateTemplates(state, { payload: template }) {
            state.templates.forEach((item, i, arr) => {
                if (item.id == template.id) {
                    arr[i] = template;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUpgradeCardApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchUpgradeCardApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleUpgradeCardPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleUpgradeCardPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createUpgradeCardApi.fulfilled, (state, { payload }) => {
                state.emptyModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.allPictures = payload.allPictures;
                state.templates = payload.templates;
            })
            .addCase(createUpgradeCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(storeUpgradeCardApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeUpgradeCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showUpgradeCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.allPictures = payload.allPictures;
                state.templates = payload.templates;
            })
            .addCase(showUpgradeCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(updateUpgradeCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make ActionUpdate re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
            })
            .addCase(updateUpgradeCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deleteUpgradeCardItemApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});
export const { clearEditedUpgradeCard, updateTemplates } = upgradeCardSlice.actions;
